import { Save } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import { DialogTitleWithCloseIcon } from 'src/components/organisms/DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';
import { useChainFurnitureZoneSaveChanges } from 'src/hooks/chain-furniture-zone/save-changes/useChainFurnitureZoneSaveChanges';
import { useChainFurnitureZoneUpdateChanges } from 'src/hooks/chain-furniture-zone/save-changes/useChainFurnitureZoneUpdateChanges';
import { ChainFurnitureZoneRow } from 'src/hooks/chain-furniture-zone/table/useChainFurnitureZoneRows';
import { useDiscardShortcut } from 'src/hooks/shortcuts/useDiscardShortcut';
import { useSaveShortcut } from 'src/hooks/shortcuts/useSaveShortcut';
import { components } from 'src/infrastructure/rest-api/generated';

type FurnitureZoneData = components['schemas']['CreateChainFurnitureZoneInput'];

interface AddFurnitureZoneDialogProps {
    isOpen: boolean;
    close: () => void;
    furnitureZoneRow?: ChainFurnitureZoneRow | null;
}

const emptyFurnitureZone: FurnitureZoneData = { name: '', description: '' };

const toFurnitureZoneData = (row?: ChainFurnitureZoneRow | null): FurnitureZoneData => {
    return row ? { name: row.name, description: row.description } : emptyFurnitureZone;
};

export const AddFurnitureZoneDialog: FC<AddFurnitureZoneDialogProps> = ({ isOpen, close, furnitureZoneRow }) => {
    const [saveFurnitureZone, { loading: savingFurnitureZone }] = useChainFurnitureZoneSaveChanges();
    const [updateFurnitureZone, { loading: updatingFurnitureZone }] = useChainFurnitureZoneUpdateChanges();
    const [furnitureZoneData, setFurnitureZoneData] = useState<FurnitureZoneData>(() =>
        toFurnitureZoneData(furnitureZoneRow)
    );

    const handleInputChange = (field: keyof FurnitureZoneData) => (event: ChangeEvent<HTMLInputElement>) => {
        setFurnitureZoneData((current) => ({ ...current, [field]: event.target.value }));
    };

    const handleSave = async () => {
        if (furnitureZoneRow?.id) {
            await updateFurnitureZone({ id: furnitureZoneRow.id, ...furnitureZoneData });
        } else {
            await saveFurnitureZone(furnitureZoneData);
        }
        close();
    };

    const reset = useCallback(() => {
        setFurnitureZoneData(toFurnitureZoneData(furnitureZoneRow));
    }, [furnitureZoneRow]);

    useSaveShortcut(handleSave, false);
    useDiscardShortcut(reset, false);

    return (
        <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={close}>
            <DialogTitleWithCloseIcon
                title={furnitureZoneRow ? 'Edit Furniture Zone' : 'Add New Furniture Zone'}
                handleClose={close}
            />
            <DialogContent>
                <Stack spacing={2}>
                    <TextField
                        id="name"
                        value={furnitureZoneData.name}
                        label="Name"
                        variant="outlined"
                        onChange={handleInputChange('name')}
                    />
                    <TextField
                        id="description"
                        value={furnitureZoneData.description}
                        label="Description"
                        variant="outlined"
                        onChange={handleInputChange('description')}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={close}>
                    Cancel
                </Button>
                <Button
                    color="info"
                    loading={savingFurnitureZone || updatingFurnitureZone}
                    loadingPosition="start"
                    startIcon={<Save />}
                    variant="contained"
                    type="submit"
                    onClick={handleSave}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
